import { v4 as uuidv4 } from "uuid";
import i18n from "../../../../lang/i18n";
import { getApiErrorCode, getApiErrorMessage } from "../../../../utils/apiError";
import { isBlankHtml } from "../../../../components/KaTeXComponent/RichText";
import { isDifficulty } from "../../../../components/Difficulty/Difficulty";
import { defaultSelectStyles } from "../../../../components/forms/Select/styles";

export const ALP = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export default function questionReducer(state, action) {
  switch (action.type) {
    // The questions as loaded (an edit page's question, again after a reload).
    case "RESET":
      return action.questions;

    case "ADD_QUESTION":
      return [...state, action.newQuestion];

    case "REMOVE_QUESTION":
      return state.filter((q) => q.id !== action.id);

    case "UPDATE_QUESTION_FIELD":
      return state.map((q) =>
        q.id === action.id ? { ...q, [action.field]: action.value } : q
      );

    case "UPDATE_OPTION_FIELD":
      return state.map((q) =>
        q.id === action.id
          ? {
              ...q,
              answer_keys: q.answer_keys.map((opt, idx) =>
                idx === action.optionIndex
                  ? { ...opt, [action.field]: action.value }
                  : opt
              ),
            }
          : q
      );

    case "ADD_OPTION":
      return state.map((q) =>
        q.id === action.id
          ? {
              ...q,
              answer_keys: [
                ...q.answer_keys,
                {
                  uuid: uuidv4(),
                  option: ALP[q.answer_keys.length % ALP.length],
                  body: ALP[q.answer_keys.length % ALP.length],
                  is_correct: false,
                },
              ],
            }
          : q
      );

    case "REMOVE_OPTION":
      return state.map((q) =>
        q.id === action.id
          ? {
              ...q,
              answer_keys: q.answer_keys
                .filter((_, idx) => idx !== action.optionIndex)
                .map((opt, index) => ({
                  ...opt,
                  option: ALP[index % ALP.length],
                })),
            }
          : q
      );

    default:
      return state;
  }
}

// What a question in an exam's subject needs before it goes to review, in the server's order and names
// (question_fields.<field>; 400 incomplete reports them). Outside folders none of this is checked.
export const REQUIRED_FIELDS = ["subtopic", "difficulty", "body", "answer_keys", "correct_answer"];

// The message under each field the server can report missing.
const MISSING_MESSAGES = {
  subtopic: "my_questions.validation.subtopic_required",
  difficulty: "my_questions.validation.difficulty_required",
  body: "my_questions.validation.body_required",
  answer_keys: "my_questions.validation.min_two_filled",
  correct_answer: "my_questions.validation.correct_answer_required",
};

// The required fields `question` lacks, as the server counts them: a picture is content, and of the options
// only the filled ones count (at least two, one of them correct).
export const missingFields = (question) => {
  const filled = (question?.answer_keys || []).filter((key) => !isBlankHtml(key?.body));
  const missing = {
    subtopic: !question?.subtopic,
    difficulty: !isDifficulty(question?.difficulty),
    body: isBlankHtml(question?.body),
    answer_keys: filled.length < 2,
    correct_answer: !filled.some((key) => key.is_correct),
  };
  return REQUIRED_FIELDS.filter((field) => missing[field]);
};

// A question's errors for the fields a 400 incomplete reports missing: the message under each field, and the
// topic picker marked too when no topic is picked (the subtopic picker opens after it).
export const missingErrors = (missing, question) => {
  const errors = {};
  (missing || []).forEach((field) => {
    errors[field] = MISSING_MESSAGES[field] || "errors.incomplete";
  });
  if (errors.subtopic && !question?.topic) errors.topic = true;
  return errors;
};

// The fields a 400 incomplete reports missing, by the index of the question in what was sent:
// {items: [{index, missing}]} for a created list (all or nothing), {missing} for one question (a draft's
// submit, an edit), which is index 0. null for any other error.
export const incompleteByIndex = (err) => {
  if (getApiErrorCode(err) !== "incomplete") return null;
  const data = err?.response?.data || {};
  if (Array.isArray(data.items)) {
    return Object.fromEntries(data.items.map((item) => [item.index, item.missing || []]));
  }
  return { 0: data.missing || [] };
};

// Errors by question index ({} when all is well), each a translation key per field (`topic` only marks
// its picker). Every question needs a text and at least two options, each filled, one of them marked
// correct; in an exam's subject (`inSubject`) also what the server checks there (REQUIRED_FIELDS).
// `allowMissing`: required fields that may stay empty, the gaps an edited question already had (the
// server only refuses an edit that empties a filled one).
export const validateQuestions = (questions, { inSubject = false, allowMissing = [] } = {}) => {
  const errorsList = {};
  questions?.forEach((question, i) => {
    const errors = {};
    const keys = question?.answer_keys || [];
    if (isBlankHtml(question?.body)) {
      errors.body = "my_questions.validation.body_required";
    }
    if (keys.length < 2) {
      errors.answer_keys = "my_questions.validation.min_two_answers";
    }
    if (!keys.some((key) => key.is_correct)) {
      errors.correct_answer = "my_questions.validation.correct_answer_required";
    }
    keys.forEach((key) => {
      if (isBlankHtml(key?.body)) {
        errors[`answer_${key?.uuid}`] = "my_questions.validation.answer_required";
      }
    });
    if (inSubject) {
      const missing = missingFields(question).filter((field) => !errors[field] && !allowMissing.includes(field));
      Object.assign(errors, missingErrors(missing, question));
    }

    if (Object.keys(errors).length > 0) {
      errorsList[i] = errors;
    }
  });
  return errorsList;
};

// The required fields a question's errors mark, for the summary at the top of its card.
export const errorFields = (errors) => REQUIRED_FIELDS.filter((field) => errors?.[field]);

// "тақырыпша, күрделілік"
export const fieldList = (t, fields) =>
  fields.map((field) => t(`question_fields.${field}`, { defaultValue: field })).join(", ");

// The question fields whose error goes once they are filled again.
const CLEARED_FIELDS = new Set(["subtopic", "topic", "difficulty", "body", "purpose", "objective"]);
const isFilled = (field, value) =>
  field === "body" ? !isBlankHtml(value) : value !== null && value !== undefined && value !== "";

// A question's errors once the user changes something: the error of what was filled goes (the save checks
// everything again). `change`: {field, value} of the question, {option: uuid, field, value} of an option,
// or {addOption: true}.
export const clearQuestionErrors = (errors, change) => {
  if (!errors) return errors;
  const next = { ...errors };
  if (change.addOption) {
    delete next.answer_keys;
  } else if (change.option !== undefined) {
    if (change.field === "body" && isFilled("body", change.value)) {
      delete next[`answer_${change.option}`];
      delete next.answer_keys;
    } else if (change.field === "is_correct" && change.value) {
      delete next.correct_answer;
    }
  } else if (CLEARED_FIELDS.has(change.field) && isFilled(change.field, change.value)) {
    delete next[change.field];
    if (change.field === "subtopic") delete next.topic;
  }
  return next;
};

// The handlers a QuestionCard edits question `id` with: the change goes into the reducer and the error of
// what was filled goes from `errors` (kept by question id).
export const cardHandlers = (id, question, dispatch, setErrors) => {
  const clear = (change) =>
    setErrors((prev) => (prev?.[id] ? { ...prev, [id]: clearQuestionErrors(prev[id], change) } : prev));
  return {
    handleQuestionUpdate: (field, value) => {
      dispatch({ type: "UPDATE_QUESTION_FIELD", id, field, value });
      clear({ field, value });
    },
    handleOptionUpdate: (optionIndex, field, value) => {
      dispatch({ type: "UPDATE_OPTION_FIELD", id, optionIndex, field, value });
      clear({ option: question?.answer_keys?.[optionIndex]?.uuid, field, value });
    },
    addOption: () => {
      dispatch({ type: "ADD_OPTION", id });
      clear({ addOption: true });
    },
    removeOption: (optionIndex) => dispatch({ type: "REMOVE_OPTION", id, optionIndex }),
  };
};

// Brings the first field marked invalid (data-invalid) into view, once the errors are on the page.
export const scrollToFirstInvalid = () => {
  window.setTimeout(() => {
    const field = document.querySelector('[data-invalid="true"]');
    if (!field) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    field.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
  }, 60);
};

// react-select look of a picker the save found empty.
export const invalidSelectStyles = {
  ...defaultSelectStyles,
  control: (provided, state) => ({
    ...defaultSelectStyles.control(provided, state),
    borderColor: state.isFocused ? "#dc2626" : "#f87171",
    boxShadow: state.isFocused ? "0 0 0 4px rgb(220 38 38 / 0.15)" : "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    "&:hover": { borderColor: "#dc2626" },
  }),
};

// Toasts for the optional fields left empty. In an exam's subject the topic and subtopic are required
// (an error, not a warning), unless `inSubject` is false.
export const getQuestionWarnings = (questions, { inSubject = false } = {}) => {
  const warnings = [];
  questions?.forEach((question, i) => {
    const missing = [];
    if (!question?.purpose) missing.push("purpose");
    if (!inSubject && !question?.topic) missing.push("topic");
    if (!inSubject && !question?.subtopic) missing.push("subtopic");
    if (!question?.objective) missing.push("objective");
    if (missing.length > 0) {
      warnings.push(
        i18n.t("my_questions.validation.not_selected", {
          number: i + 1,
          fields: missing
            .map((field) => i18n.t(`my_questions.validation.fields.${field}`))
            .join(", "),
        }),
      );
    }
  });
  return warnings;
};

// Message for a failed save: field errors ({subject_uuid: [...]}, also per question in a list) first,
// then the translated error code / server detail, then the generic fallback.
export const getSaveErrorMessage = (t, err) => {
  const data = err?.response?.data;
  const items = Array.isArray(data) ? data : [data];
  for (const item of items) {
    if (item && typeof item === "object" && !item.code && !item.detail) {
      const message = Object.values(item)
        .flat()
        .find((v) => typeof v === "string" && v.trim());
      if (message) return message;
    }
  }
  return getApiErrorMessage(t, err, "my_questions.toasts.something_went_wrong");
};
